import RouteMetadata from "@/components/RouteMetadata";
import "@/App.css";
import React, { useEffect, Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { CustomerAuthProvider } from "@/context/CustomerAuthContext";
import { CartProvider } from "@/context/CartContext";
import Storefront from "@/pages/Storefront";
import AdminLayout from "@/components/AdminLayout";
import { initSyncEngine } from "@/lib/syncEngine";

// Code splitting: Lazy load secondary routes so initial homepage bundle is super small and fast
const Checkout = lazy(() => import("@/pages/Checkout"));
const TrackOrder = lazy(() => import("@/pages/TrackOrder"));
const Login = lazy(() => import("@/pages/Login"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Pos = lazy(() => import("@/pages/Pos"));
const Orders = lazy(() => import("@/pages/Orders"));
const MenuAdmin = lazy(() => import("@/pages/MenuAdmin"));
const OffersAdmin = lazy(() => import("@/pages/OffersAdmin"));
const StaffAdmin = lazy(() => import("@/pages/StaffAdmin"));
const CustomerAdmin = lazy(() => import("@/pages/CustomerAdmin"));
const TableAdmin = lazy(() => import("@/pages/TableAdmin"));
const Kitchen = lazy(() => import("@/pages/Kitchen"));
const DeliveryApp = lazy(() => import("@/pages/DeliveryApp"));
const LiveTracking = lazy(() => import("@/pages/LiveTracking"));
const CustomerAccount = lazy(() => import("@/pages/CustomerAccount"));
const LegalPolicies = lazy(() => import("@/pages/LegalPolicies"));

const PageLoading = () => (
  <div className="min-h-[50vh] flex flex-col items-center justify-center p-8 space-y-3">
    <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
    <span className="text-xs uppercase font-bold tracking-widest text-muted-foreground">Loading...</span>
  </div>
);

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error("App Error caught by ErrorBoundary:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-background text-foreground flex flex-col items-center justify-center p-6 text-center space-y-4">
          <img src="/logo.png" alt="Logo" className="w-16 h-16 rounded-full border border-foreground/20" />
          <h2 className="text-2xl font-bold text-primary">Something went wrong</h2>
          <p className="text-sm text-muted-foreground max-w-md">
            The application encountered a display error. Tap below to reload the session fresh.
          </p>
          {this.state.error && (
            <pre className="text-xs bg-red-100 text-red-900 p-3 rounded max-w-xl overflow-x-auto text-left font-mono border border-red-300">
              {this.state.error?.toString()}
            </pre>
          )}
          <button
            onClick={() => {
              this.setState({ hasError: false, error: null });
              window.location.reload();
            }}
            className="border-2 border-foreground bg-primary text-primary-foreground font-bold px-6 py-2.5 rounded text-xs uppercase tracking-wider hover:opacity-90 shadow"
          >
            Reload POS Terminal
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

function Protected({ children, adminOnly, bare }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading)
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background text-foreground space-y-3" data-testid="auth-loading">
        <div className="relative">
          <img src="/logo.png" alt="Logo" className="w-14 h-14 rounded-full border border-foreground/20" />
          <div className="absolute -inset-1.5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
        <span className="text-xs uppercase font-bold tracking-widest text-muted-foreground mt-2">
          Opening Staff Terminal…
        </span>
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "delivery" && !bare) {
    return <Navigate to="/delivery" replace />;
  }
  if (user.role !== "delivery" && bare && location.pathname === "/delivery") {
    return <Navigate to="/admin/pos" replace />;
  }
  if (adminOnly && user.role !== "admin") return <Navigate to="/admin/pos" replace />;
  if (bare) return <ErrorBoundary key={location.pathname}>{children}</ErrorBoundary>;
  return <AdminLayout><ErrorBoundary key={location.pathname}>{children}</ErrorBoundary></AdminLayout>;
}

export default function App() {
  useEffect(() => {
    initSyncEngine();
  }, []);

  return (
    <AuthProvider>
      <CustomerAuthProvider>
        <CartProvider>
          <BrowserRouter>
            <RouteMetadata />
            <Toaster position="top-right" />
            <Suspense fallback={<PageLoading />}>
              <Routes>
                <Route path="/" element={<Storefront />} />
                <Route path="/checkout" element={<Checkout />} />
                <Route path="/track" element={<TrackOrder />} />
                <Route path="/account" element={<CustomerAccount />} />
                <Route path="/terms" element={<LegalPolicies />} />
                <Route path="/terms-and-conditions" element={<LegalPolicies />} />
                <Route path="/privacy" element={<LegalPolicies />} />
                <Route path="/privacy-policy" element={<LegalPolicies />} />
                <Route path="/refund-policy" element={<LegalPolicies />} />
                <Route path="/cancellation-policy" element={<LegalPolicies />} />
                <Route path="/shipping-policy" element={<LegalPolicies />} />
                <Route path="/delivery-policy" element={<LegalPolicies />} />
                <Route path="/contact" element={<LegalPolicies />} />
                <Route path="/contact-us" element={<LegalPolicies />} />
                <Route path="/login" element={<Login />} />
                <Route path="/delivery" element={<Protected bare><DeliveryApp /></Protected>} />
                <Route path="/admin" element={<Navigate to="/admin/pos" replace />} />
                <Route path="/admin/pos" element={<Protected><Pos /></Protected>} />
                <Route path="/admin/orders" element={<Protected><Orders /></Protected>} />
                <Route path="/admin/live-tracking" element={<Protected><LiveTracking /></Protected>} />
                <Route path="/admin/dashboard" element={<Protected adminOnly><Dashboard /></Protected>} />
                <Route path="/admin/customers" element={<Protected adminOnly><CustomerAdmin /></Protected>} />
                <Route path="/admin/menu" element={<Protected adminOnly><MenuAdmin /></Protected>} />
                <Route path="/admin/offers" element={<Protected adminOnly><OffersAdmin /></Protected>} />
                <Route path="/admin/staff" element={<Protected adminOnly><StaffAdmin /></Protected>} />
                <Route path="/admin/tables" element={<Protected adminOnly><TableAdmin /></Protected>} />
                <Route path="/kitchen" element={<Protected bare><Kitchen /></Protected>} />
                <Route path="*" element={<main className="min-h-screen grid place-content-center gap-4 p-8"><h1 className="text-3xl">Page not found</h1><p>This address does not match a page on our website.</p><a href="/" className="underline">Return to the menu</a></main>} />
              </Routes>
            </Suspense>
          </BrowserRouter>
        </CartProvider>
      </CustomerAuthProvider>
    </AuthProvider>
  );
}

